import { useState, useEffect } from "react";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "../lib/trpc";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Plus, Clock, Pencil, Trash2 } from "lucide-react";
import { useLocation } from "wouter";

export default function QuoteConfig() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [, setLocation] = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isEmitenteModalOpen, setIsEmitenteModalOpen] = useState(false);
  const [isNatureModalOpen, setIsNatureModalOpen] = useState(false);
  const [natureSearchTerm, setNatureSearchTerm] = useState("");
  const [isPrazosModalOpen, setIsPrazosModalOpen] = useState(false);
  const [prazosQuoteTypeId, setPrazosQuoteTypeId] = useState<number | null>(null);
  const [prazosQuoteTypeName, setPrazosQuoteTypeName] = useState("");
  const [novoPrazo, setNovoPrazo] = useState({ qtdSkuMax: "", prazoMaximo: "" });
  const [editingPrazoId, setEditingPrazoId] = useState<number | null>(null);
  const [editingPrazoData, setEditingPrazoData] = useState({ qtdSkuMax: "", prazoMaximo: "" });
  const [padraoPortal, setPadraoPortal] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: "",
    emitenteId: 0,
    emitenteNome: "",
    prazoPreparo: "000:00",
    tipo: "b2b" as "b2b" | "b2c" | "transferencia" | "amostra" | "ajuste",
    valorMinimo: 0,
    observacoes: "",
    validarLimite: false,
    validarMargem: false,
    margemMinima: 0,
    validarFinanceiro: false,
    diasAtrasoMaximo: 0,
    emitirNota: false,
    emitirRemessa: false,
    ativo: true,
    operationNatureId: null as number | null,
    operationNatureName: "",
  });

  // Auth redirect
  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  // Queries
  const { data: quoteTypes = [], refetch } = trpc.quoteTypes.list.useQuery();
  const { data: emitentes = [] } = trpc.emitentes.list.useQuery();
  const { data: operationNatures = [] } = trpc.operationNatures.list.useQuery();
  const configPortal = trpc.portalConfig.tipoOrcamento.get.useQuery(
    { quoteTypeId: editingId || 0 },
    { enabled: !!editingId && isDialogOpen }
  );
  const { data: prazos = [], refetch: refetchPrazos } = trpc.quoteTypes.prazos.list.useQuery(
    { quoteTypeId: prazosQuoteTypeId ?? 0 },
    { enabled: !!prazosQuoteTypeId }
  );

  // Mutations
  const salvarConfigPortal = trpc.portalConfig.tipoOrcamento.save.useMutation({
    onError: (error) => toast.error(error.message || "Erro ao salvar Padrão portal."),
  });
  const createMutation = trpc.quoteTypes.create.useMutation({
    onSuccess: (resultado: any) => {
      if (resultado?.id) salvarConfigPortal.mutate({ quoteTypeId: resultado.id, padraoPortal });
      toast.success("Tipo de orçamento criado com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao criar tipo de orçamento");
    },
  });

  const updateMutation = trpc.quoteTypes.update.useMutation({
    onSuccess: () => {
      if (editingId) salvarConfigPortal.mutate({ quoteTypeId: editingId, padraoPortal });
      toast.success("Tipo de orçamento atualizado com sucesso!");
      setIsDialogOpen(false);
      setEditingId(null);
      resetForm();
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar tipo de orçamento");
    },
  });

  useEffect(() => {
    if (configPortal.data) setPadraoPortal(Boolean(configPortal.data.padraoPortal));
  }, [configPortal.data]);

  const createPrazoMutation = trpc.quoteTypes.prazos.create.useMutation({
    onSuccess: () => { setNovoPrazo({ qtdSkuMax: "", prazoMaximo: "" }); refetchPrazos(); },
    onError: (error) => { toast.error(error.message || "Erro ao criar prazo"); },
  });
  const updatePrazoMutation = trpc.quoteTypes.prazos.update.useMutation({
    onSuccess: () => { setEditingPrazoId(null); refetchPrazos(); },
    onError: (error) => { toast.error(error.message || "Erro ao atualizar prazo"); },
  });
  const deletePrazoMutation = trpc.quoteTypes.prazos.delete.useMutation({
    onSuccess: () => { refetchPrazos(); },
    onError: (error) => { toast.error(error.message || "Erro ao excluir prazo"); },
  });

  const toggleAtivoMutation = trpc.quoteTypes.update.useMutation({
    onSuccess: () => {
      toast.success("Status atualizado com sucesso!");
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao atualizar status");
    },
  });

  // Funções de conversão de prazo
  const prazoToMinutes = (prazo: string): number => {
    const [horas, minutos] = prazo.split(":").map(Number);
    return (horas || 0) * 60 + (minutos || 0);
  };

  const minutesToPrazo = (minutes: number): string => {
    const horas = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${String(horas).padStart(3, "0")}:${String(mins).padStart(2, "0")}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { emitenteNome, operationNatureName, ...rest } = formData;
    const dataToSend = {
      ...rest,
      prazoPreparo: prazoToMinutes(formData.prazoPreparo),
    };
    
    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, ...dataToSend });
    } else {
      await createMutation.mutateAsync(dataToSend);
    }
  };

  const resetForm = () => {
    setPadraoPortal(false);
    setFormData({
      nome: "",
      emitenteId: 0,
      emitenteNome: "",
      prazoPreparo: "000:00",
      tipo: "b2b",
      valorMinimo: 0,
      observacoes: "",
      validarLimite: false,
      validarMargem: false,
      margemMinima: 0,
      validarFinanceiro: false,
      diasAtrasoMaximo: 0,
      emitirNota: false,
      emitirRemessa: false,
      ativo: true,
      operationNatureId: null,
      operationNatureName: "",
    });
  };

  const handleEdit = (quoteType: any) => {
    setPadraoPortal(false);
    // Buscar nome da natureza de operação se existir
    let natureName = "";
    if (quoteType.operationNatureId) {
      const nature = operationNatures.find((n: any) => n.id === quoteType.operationNatureId);
      if (nature) {
        natureName = `${nature.nome} - ${nature.tipo}`;
      }
    }

    setFormData({
      nome: quoteType.nome,
      emitenteId: quoteType.emitenteId || 0,
      emitenteNome: quoteType.emitenteNome || "",
      prazoPreparo: minutesToPrazo(quoteType.prazoPreparo),
      tipo: quoteType.tipo,
      valorMinimo: parseFloat(quoteType.valorMinimo) || 0,
      observacoes: quoteType.observacoes || "",
      validarLimite: Boolean(quoteType.validarLimite),
      validarMargem: Boolean(quoteType.validarMargem),
      margemMinima: parseFloat(quoteType.margemMinima) || 0,
      validarFinanceiro: Boolean(quoteType.validarFinanceiro),
      diasAtrasoMaximo: parseInt(quoteType.diasAtrasoMaximo) || 0,
      emitirNota: Boolean(quoteType.emitirNota),
      emitirRemessa: Boolean(quoteType.emitirRemessa),
      ativo: Boolean(quoteType.ativo),
      operationNatureId: quoteType.operationNatureId || null,
      operationNatureName: natureName,
    });
    setEditingId(quoteType.id);
    setIsDialogOpen(true);
  };

  const handleToggleAtivo = async (id: number, currentStatus: boolean) => {
    await toggleAtivoMutation.mutateAsync({ id, ativo: !currentStatus });
  };

  // Loading e auth checks
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (user.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Acesso Negado</CardTitle>
            <CardDescription>
              Apenas administradores podem acessar esta página.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setLocation("/")}>Voltar para Início</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold">Configuração de Orçamentos</h1>
          <Button
            onClick={() => {
              resetForm();
              setEditingId(null);
              setIsDialogOpen(true);
            }}
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Tipo
          </Button>
        </div>

        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="border-b p-3 text-center">Ativo</th>
                <th className="border-b p-3 text-center">ID</th>
                <th className="border-b p-3 text-center">Descrição</th>
                <th className="border-b p-3 text-center">Emitente</th>
                <th className="border-b p-3 text-center">Tipo</th>
                <th className="border-b p-3 text-center">Valor Mínimo</th>
                <th className="border-b p-3 text-center">Criação</th>
                <th className="border-b p-3 text-center">Última Alteração</th>
              </tr>
            </thead>
            <tbody>
              {quoteTypes.map((qt: any) => (
                <tr
                  key={qt.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleEdit(qt)}
                >
                  <td className="border-b p-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => handleToggleAtivo(qt.id, qt.ativo)}
                      className={`w-12 h-6 rounded-full transition-colors ${
                        qt.ativo ? "bg-green-500" : "bg-gray-300"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 bg-white rounded-full transition-transform ${
                          qt.ativo ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </td>
                  <td className="border-b p-3 text-center">{qt.id}</td>
                  <td className="border-b p-3 text-center">{qt.nome}</td>
                  <td className="border-b p-3 text-center">{qt.emitenteNome || "-"}</td>
                  <td className="border-b p-3 text-center">{qt.tipo.toUpperCase()}</td>
                  <td className="border-b p-3 text-center">
                    R$ {qt.valorMinimo.toFixed(2)}
                  </td>
                  <td className="border-b p-3 text-center">
                    {new Date(qt.criadoEm).toLocaleDateString("pt-BR")}
                  </td>
                  <td className="border-b p-3 text-center">
                    {new Date(qt.atualizadoEm).toLocaleDateString("pt-BR")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar Tipo de Orçamento" : "Novo Tipo de Orçamento"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Nome do Tipo *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>Emitente *</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.emitenteNome}
                  readOnly
                  placeholder="Selecione um emitente"
                  required
                />
                <Button
                  type="button"
                  onClick={() => setIsEmitenteModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
              </div>
            </div>

            <div>
              <Label>Natureza de Operação</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.operationNatureName}
                  readOnly
                  placeholder="Selecione uma natureza de operação"
                />
                <Button
                  type="button"
                  onClick={() => setIsNatureModalOpen(true)}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  Selecionar
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prazos de Preparo</Label>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full flex items-center gap-2"
                  disabled={!editingId}
                  onClick={() => {
                    if (editingId) {
                      const qt = quoteTypes.find((q: any) => q.id === editingId);
                      setPrazosQuoteTypeId(editingId);
                      setPrazosQuoteTypeName(qt?.nome || "");
                      setIsPrazosModalOpen(true);
                    }
                  }}
                >
                  <Clock className="h-4 w-4" />
                  {editingId ? "Prazos de Preparo" : "Salve primeiro para configurar"}
                </Button>
              </div>

              <div>
                <Label>Tipo *</Label>
                <Select
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({ ...formData, tipo: value as any })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="b2b">B2B</SelectItem>
                    <SelectItem value="b2c">B2C</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                    <SelectItem value="amostra">Amostra</SelectItem>
                    <SelectItem value="ajuste">Ajuste</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Valor Mínimo (R$) *</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.valorMinimo}
                onChange={(e) => setFormData({ ...formData, valorMinimo: parseFloat(e.target.value) || 0 })}
                required
              />
            </div>

            <div>
              <Label>Observações</Label>
              <Textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.validarLimite}
                  onChange={(e) => setFormData({ ...formData, validarLimite: e.target.checked })}
                />
                Validar Limite
              </Label>

              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.validarMargem}
                  onChange={(e) => setFormData({ ...formData, validarMargem: e.target.checked })}
                />
                Validar Margem
              </Label>

              <div className="ml-6">
                <Label className={!formData.validarMargem ? "text-muted-foreground" : ""}>Margem Mínima (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.margemMinima}
                  onChange={(e) => setFormData({ ...formData, margemMinima: parseFloat(e.target.value) || 0 })}
                  placeholder="Ex: 15.50"
                  disabled={!formData.validarMargem}
                  className={!formData.validarMargem ? "opacity-50" : ""}
                />
              </div>

              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.validarFinanceiro}
                  onChange={(e) => setFormData({ ...formData, validarFinanceiro: e.target.checked })}
                />
                Validar Financeiro
              </Label>

              <div className="ml-6">
                <Label className={!formData.validarFinanceiro ? "text-muted-foreground" : ""}>Dias de Atraso Máximo</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.diasAtrasoMaximo}
                  onChange={(e) => setFormData({ ...formData, diasAtrasoMaximo: parseInt(e.target.value) || 0 })}
                  placeholder="Ex: 30"
                  disabled={!formData.validarFinanceiro}
                  className={!formData.validarFinanceiro ? "opacity-50" : ""}
                />
              </div>

              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.emitirNota}
                  onChange={(e) => setFormData({ ...formData, emitirNota: e.target.checked })}
                />
                Emitir Nota
              </Label>

              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.emitirRemessa}
                  onChange={(e) => setFormData({ ...formData, emitirRemessa: e.target.checked })}
                />
                Emitir Remessa
              </Label>

              <Label className="flex items-center gap-2">
                <input type="checkbox" checked={padraoPortal} onChange={(e) => setPadraoPortal(e.target.checked)} />
                Padrão portal
              </Label>

            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                {editingId ? "Atualizar" : "Criar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Emitente */}
      <Dialog open={isEmitenteModalOpen} onOpenChange={setIsEmitenteModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Selecionar Emitente</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-x-auto overflow-y-auto">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  <th className="border-b p-3 text-left">ID</th>
                  <th className="border-b p-3 text-left">Razão Social</th>
                  <th className="border-b p-3 text-left">CNPJ</th>
                  <th className="border-b p-3 text-center">Ação</th>
                </tr>
              </thead>
              <tbody>
                {emitentes.map((em: any) => (
                  <tr key={em.id} className="hover:bg-gray-50">
                    <td className="border-b p-3">{em.id}</td>
                    <td className="border-b p-3">{em.razaoSocial}</td>
                    <td className="border-b p-3">{em.cnpj}</td>
                    <td className="border-b p-3 text-center">
                      <Button
                        size="sm"
                        onClick={() => {
                          setFormData({ ...formData, emitenteId: em.id, emitenteNome: em.razaoSocial });
                          setIsEmitenteModalOpen(false);
                        }}
                        className="bg-orange-500 hover:bg-orange-600"
                      >
                        Selecionar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Prazos de Preparo por SKU */}
      <Dialog open={isPrazosModalOpen} onOpenChange={setIsPrazosModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Prazos de Preparo — {prazosQuoteTypeName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="max-h-[40vh] overflow-x-auto overflow-y-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className="border-b p-3 text-center">ID</th>
                    <th className="border-b p-3 text-center">Qtd SKU Máx</th>
                    <th className="border-b p-3 text-center">Prazo Máximo</th>
                    <th className="border-b p-3 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {(prazos as any[]).map((p: any) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="border-b p-3 text-center">{p.id}</td>
                      <td className="border-b p-3 text-center">
                        {editingPrazoId === p.id ? (
                          <Input
                            type="number"
                            min={1}
                            value={editingPrazoData.qtdSkuMax}
                            onChange={(e) => setEditingPrazoData({ ...editingPrazoData, qtdSkuMax: e.target.value })}
                            className="w-24 mx-auto"
                          />
                        ) : p.qtdSkuMax}
                      </td>
                      <td className="border-b p-3 text-center">
                        {editingPrazoId === p.id ? (
                          <Input
                            type="text"
                            value={editingPrazoData.prazoMaximo}
                            onChange={(e) => setEditingPrazoData({ ...editingPrazoData, prazoMaximo: e.target.value })}
                            placeholder="HHH:MM:SS"
                            className="w-32 mx-auto"
                          />
                        ) : p.prazoMaximo}
                      </td>
                      <td className="border-b p-3 text-center">
                        <div className="flex gap-2 justify-center">
                          {editingPrazoId === p.id ? (
                            <>
                              <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={() => {
                                updatePrazoMutation.mutate({
                                  id: p.id,
                                  qtdSkuMax: parseInt(editingPrazoData.qtdSkuMax),
                                  prazoMaximo: editingPrazoData.prazoMaximo,
                                });
                              }}>Salvar</Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingPrazoId(null)}>Cancelar</Button>
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="outline" onClick={() => {
                                setEditingPrazoId(p.id);
                                setEditingPrazoData({ qtdSkuMax: String(p.qtdSkuMax), prazoMaximo: p.prazoMaximo });
                              }}><Pencil className="h-4 w-4" /></Button>
                              <Button size="sm" variant="outline" className="text-red-500 hover:text-red-700" onClick={() => deletePrazoMutation.mutate({ id: p.id })}><Trash2 className="h-4 w-4" /></Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(prazos as any[]).length === 0 && (
                    <tr><td colSpan={4} className="p-4 text-center text-gray-400">Nenhum prazo cadastrado</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-2">Adicionar novo prazo</p>
              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <Label className="text-xs">Qtd SKU Máx</Label>
                  <Input
                    type="number"
                    min={1}
                    value={novoPrazo.qtdSkuMax}
                    onChange={(e) => setNovoPrazo({ ...novoPrazo, qtdSkuMax: e.target.value })}
                    placeholder="Ex: 10"
                    className="w-28"
                  />
                </div>
                <div>
                  <Label className="text-xs">Prazo Máximo (HHH:MM:SS)</Label>
                  <Input
                    type="text"
                    value={novoPrazo.prazoMaximo}
                    onChange={(e) => setNovoPrazo({ ...novoPrazo, prazoMaximo: e.target.value })}
                    placeholder="000:00:00"
                    className="w-36"
                  />
                </div>
                <Button
                  className="bg-orange-500 hover:bg-orange-600"
                  onClick={() => {
                    if (!novoPrazo.qtdSkuMax || !novoPrazo.prazoMaximo) { toast.error("Preencha todos os campos"); return; }
                    createPrazoMutation.mutate({
                      quoteTypeId: prazosQuoteTypeId!,
                      qtdSkuMax: parseInt(novoPrazo.qtdSkuMax),
                      prazoMaximo: novoPrazo.prazoMaximo,
                    });
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" /> Adicionar
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Natureza de Operação */}
      <Dialog open={isNatureModalOpen} onOpenChange={setIsNatureModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Selecionar Natureza de Operação</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Buscar por nome, tipo, ID ou emitente..."
              value={natureSearchTerm}
              onChange={(e) => setNatureSearchTerm(e.target.value)}
            />
            <div className="max-h-[50vh] overflow-x-auto overflow-y-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className="border-b p-3 text-left">ID</th>
                    <th className="border-b p-3 text-left">Nome</th>
                    <th className="border-b p-3 text-left">Tipo</th>
                    <th className="border-b p-3 text-left">Emitente</th>
                    <th className="border-b p-3 text-center">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {operationNatures
                    .filter((nature: any) =>
                      natureSearchTerm === "" ||
                      nature.nome?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                      nature.tipo?.toLowerCase().includes(natureSearchTerm.toLowerCase()) ||
                      String(nature.id).includes(natureSearchTerm) ||
                      nature.emitenteNome?.toLowerCase().includes(natureSearchTerm.toLowerCase())
                    )
                    .map((nature: any) => (
                      <tr key={nature.id} className="hover:bg-gray-50">
                        <td className="border-b p-3 text-gray-500 font-mono text-sm">{nature.id}</td>
                        <td className="border-b p-3">{nature.nome}</td>
                        <td className="border-b p-3">{nature.tipo}</td>
                        <td className="border-b p-3 text-sm text-gray-600">{nature.emitenteNome || "-"}</td>
                        <td className="border-b p-3 text-center">
                          <Button
                            size="sm"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                operationNatureId: nature.id,
                                operationNatureName: `${nature.nome} - ${nature.tipo}`,
                              });
                              setIsNatureModalOpen(false);
                              setNatureSearchTerm("");
                            }}
                            className="bg-orange-500 hover:bg-orange-600"
                          >
                            Selecionar
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
